const trapFocus = (element, prevFocusableElement = document.activeElement) => {
    const focusableEls = Array.from(
      element.querySelectorAll(
        'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
      )
    );
    if (focusableEls.length === 0) return;
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];
    let currentFocus = null;

    firstFocusableEl.focus();
    currentFocus = firstFocusableEl;

    const handleFocus = e => {
      e.preventDefault();
      // if the focused element "lives" in your modal container then just focus it
      if (focusableEls.includes(e.target)) {
        currentFocus = e.target;
      } else {
        // you're out of the container
        // if previously the focused element was the first element then focus the last 
        // element - means you were using the shift key
        if (currentFocus === firstFocusableEl) {
          lastFocusableEl.focus();
        } else {
          // you previously were focused on the last element so just focus the first one
          firstFocusableEl.focus();
        }
        // update the current focus var
        currentFocus = document.activeElement;
      }
    };

    document.addEventListener("focus", handleFocus, true);

    return {
      onClose: () => {
        document.removeEventListener("focus", handleFocus, true);
        prevFocusableElement.focus();
      }
    };
  };


(async () => {
    const Core = await importModule('Core');

    const {
        $hide,
        $show,
        fetchHTML,
        $isHidden
    } = await importModule('Utils');


    const CLASSNAME_LOAD = '@load';
    const CLASSNAME_ANIMATE = '@animate';
    const CN_NO_SCROLL = 'no-scroll';
    const CN_INVISIBLE = 'invisible';

    const ATTR_LOADING = 'loading';
    const ATTR_OPEN = 'opened';
    const ATTR_ANIMATION = 'animation';

    const S_PROP_TRANSITION = '--transition';
    const S_PROP_X = '--position-x';
    const S_PROP_Y = '--position-y';

    customElements.define('modal-popup', class extends Core.default {
        elements = {
            content:  '[data-content]',
            layout:   '[data-layout]',
            close:    '[data-close]',
            backdrop: '[data-backdrop]'
        }

        subscriptions = {
            'modal:open':  '_openModal',
            'modal:close': '_closeModal'
        }
        
        cache = {};
        currentTarget;
        isOpen = false;

        render() {
            this.subscribe('modal:open', { global: true });
            this.subscribe('modal:close', { global: true });
            this.publish('modal:ready');
            window.theme.modalReady = true;
        }

        async _openModal({
            target,
            url,
            content,
            ...props
        }) {
            if(props.scrollLock !== false) {
                this._scrollLock(true);
            }

            if (this.isOpen) {
                await this._switchModal(props.backdrop);
            }
            setTimeout(async () => {
                this._setProps(props);
                if(content) {
                    this._useContent(content);
                } else {
                    await this._setContent(target, url);
                }
                if(this.focusTrap) {
                    this.trapped = trapFocus(this.$content);
                }
                this._animate();
            }, 20)   
        };

        _closeModal() {
            if(this.updateOnClose) {
                this._updateContent();
            }
            this.classList.remove(CLASSNAME_ANIMATE);
            this.classList.remove(CLASSNAME_LOAD);
            this._transitionDelay(() => {
                this.removeAttribute(ATTR_OPEN, '');
                this.isOpen = false;
            })
            this._scrollLock(false);
            this.$content.replaceChildren();
            this.currentTarget = null;
            if(this.focusTrap) {
                this.trapped.onClose(); 
            }
        };

        _scrollLock(state) {
            document.body.classList.toggle(CN_NO_SCROLL, state);
        }

        _updateContent() {
            this.cache[this.currentTarget] = this.$content.cloneNode(true);
        }

        async _switchModal(withBackdrop) {
            return new Promise(resolve => {
                this.classList.remove(CLASSNAME_ANIMATE);

                if (withBackdrop && this.classList.contains(CLASSNAME_LOAD) || !withBackdrop ) {
                    this.classList.remove(CLASSNAME_LOAD)
                }

                this._transitionDelay(() => {
                    $hide(this.$content);                
                    this.setAttribute(ATTR_ANIMATION, '');
                    resolve();
                })
            });
        };

        _transitionDelay(callback) {
            setTimeout(callback, this.transition)
        };

        async _setContent(target, url) {
            // NOTE: no-cache logic is terrible! Needs refactoring ASAP!
            const targetSrc = url ? url + target : target;
            if(!this.cache[targetSrc] || !this.useCache) {
                const targetContent = url ? await this._fetchTarget(url, target) : document.querySelector(target);
                const displayContent = targetContent.content || targetContent;

                if(!displayContent) {
                    console.error(`Modal ${targetSrc} target element do not exists`);
                    return;
                }
                if (displayContent && this.useCache) this.cache[targetSrc] = displayContent;

                if(!this.useCache) {
                    
                    this.$content.replaceChildren(...displayContent.cloneNode(true).childNodes);
                }
            }

            if(this.currentTarget !== targetSrc && this.useCache) {
                this.cache[this.currentTarget] = this.$content.cloneNode(true);
                // TEMP: FOUC fix. This is terrible!
                if(url) {
                    this.$layout.classList.add(CN_INVISIBLE);                        
                    this.$content.replaceChildren(...this.cache[targetSrc].cloneNode(true).childNodes);
                    setTimeout(() => {
                        this.$layout.classList.remove(CN_INVISIBLE);                        
                    }, 150);
                } else {
                    this.$content.replaceChildren(...this.cache[targetSrc].cloneNode(true).childNodes);
                }
            }

            this._showContent();

            this.currentTarget = targetSrc;
        }

        _useContent(content) {
            this.$content.replaceChildren(...content.cloneNode(true).childNodes)
            this._showContent();
            this.currentTarget = content;
        }

        _showContent() {
            $isHidden(this.$content) && $show(this.$content)
        }

        async _fetchTarget(url, target) {
            this._loading(true);
            const html = await fetchHTML(url);
            this._loading(false);
            return html.querySelector(target);
        }
        
        _loading(state) {
            state 
                ? this.setAttribute(ATTR_LOADING, '') 
                : this.removeAttribute(ATTR_LOADING);
        }

        _animate() {
            setTimeout(() => {
                this.classList.add(CLASSNAME_ANIMATE);
            }, 20);
        }

        _setProps({
            transition = '300',
            animation = 'fade',
            positionX = 'center',
            positionY = 'center',
            backdrop = true,
            close = true,
            noCache,
            updateOnClose,
            focusTrap = true
        }) {
            this.focusTrap = focusTrap;
            this.useCache = !noCache;
            this.isOpen = true;
            this.transition = +transition;
            this.setAttribute(ATTR_ANIMATION, animation);
            this.setAttribute(ATTR_OPEN, '');
            this.updateOnClose = updateOnClose;
            setTimeout(() => {
                this.classList.add(CLASSNAME_LOAD);
            }, 20);

            this.style.setProperty(S_PROP_TRANSITION, `${this.transition}ms`);
            this.style.setProperty(S_PROP_X, positionX);
            this.style.setProperty(S_PROP_Y, positionY);

            backdrop 
                ? $show(this.$backdrop) 
                : $hide(this.$backdrop);

            close 
                ? $show(this.$close) 
                : $hide(this.$close);
        }

    })

    customElements.define('modal-trigger', class extends Core.default {
        render() {
            this.addEventListener('click', () => {
                this.publish('modal:open', {
                    target: this.getAttribute('target'),
                    positionY: this._setPositionY(),
                    positionX: this._setPositionX(),
                    backdrop: this.getAttribute('backdrop') !== 'false',
                    close: this.getAttribute('close') !== 'false',
                    animation: this.getAttribute('animation') || 'fade',
                    transition: this.getAttribute('transition') || 500,
                    url: this.getAttribute('url'),
                    noCache: this.hasAttribute('no-cache'),
                    updateOnClose: this.hasAttribute('updateOnClose'),
                    focusTrap: this.getAttribute('focus-trap') !== 'false'
                });
            })
        }
        _setPositionX() {
            if(!this.hasAttribute('x') || this.getAttribute('x') === 'center') return 'center';
            if(this.getAttribute('x') === 'left') return 'flex-start';
            if(this.getAttribute('x') === 'right') return 'flex-end';
        }

        _setPositionY() {
            if(!this.hasAttribute('y') || this.getAttribute('y') === 'center') return 'center';
            if(this.getAttribute('y') === 'top') return 'flex-start';
            if(this.getAttribute('y') === 'bottom') return 'flex-end';
        }
    })

    customElements.define('modal-close', class extends Core.default {
        render() {
            this.addEventListener('click', () => {
                this.publish('modal:close');
            })
        }
    });

    customElements.define('product-to-cart', class extends Core.default {
        render() {
            this.addEventListener('click', () => {
                this.publish('modal:close');
            })
        }
    });
  
})();